import clsx from "clsx"
import { FC, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Swiper, SwiperSlide } from "swiper/react"
import { Swiper as SwiperType } from "swiper/types"
import { ArrowLeftIcon } from "@/assets"
import { scrollTo } from "@/helpers/scrollTo"
import { useModal } from "@/modals"
import { useAppSelector } from "@/store"
import { INFO_MODAL_TYPES } from "@/store/apis/infoModalAPI"
import {
  useGetInfoModalHasReadQuery,
  useMarkInfoModalAsReadMutation
} from "@/store/apis/infoModalAPI/infoModal.api.ts"
import { AppSelectors } from "@/store/slices/app"
import { Button } from "@/ui/Button"
import { Modal } from "@/ui/Modal"
import styles from "./HowItWorksModal.module.scss"
import {
  HowItWorksEffectsSlide,
  HowItWorksGoalSlide,
  HowItWorksGuideSlide,
  HowItWorksHowToPlaySlide,
  HowItWorksIntroSlide,
  HowItWorksRunesSlide,
  HowItWorksScreenLayoutSlide,
  HowItWorksScreenSlide
} from "./ui/slides"

const modalType = "howItWorks"

const SLIDES = [
  { key: "intro", Component: HowItWorksIntroSlide },
  { key: "goal", Component: HowItWorksGoalSlide },
  { key: "screen", Component: HowItWorksScreenSlide },
  { key: "screenLayout", Component: HowItWorksScreenLayoutSlide },
  { key: "howToPlay", Component: HowItWorksHowToPlaySlide },
  { key: "runes", Component: HowItWorksRunesSlide },
  { key: "effects", Component: HowItWorksEffectsSlide },
  { key: "guide", Component: HowItWorksGuideSlide }
] as const

export const HowItWorksModal: FC = () => {
  const { t } = useTranslation()
  const { modal } = useModal(modalType)

  const language = useAppSelector(AppSelectors.selectLanguage())

  const [activeSlide, setActiveSlide] = useState<number>(0)

  const refSwiper = useRef<SwiperType | null>(null)

  const { data: hasRead } = useGetInfoModalHasReadQuery({
    type: INFO_MODAL_TYPES.TUTORIAL,
    lang: language
  })

  const [markInfoModalAsRead] = useMarkInfoModalAsReadMutation()

  const closeModal = () => {
    modal.close()
  }

  useEffect(() => {
    if (!hasRead) return

    if (!hasRead.hasRead) {
      markInfoModalAsRead({
        type: INFO_MODAL_TYPES.TUTORIAL,
        lang: language
      })
    }
  }, [hasRead, language])

  return (
    <Modal.Center
      type={modalType}
      className={clsx(styles.HowItWorksModal)}
      bodyWrap={{ className: styles.HowItWorksModal_bodyWrap }}
      body={{ className: styles.HowItWorksModal_body }}
      onClose={closeModal}
    >
      <Modal.StoneFlexContainer
        content={{ className: styles.HowItWorksModal_content }}
        outside={
          <>
            <div className={clsx(styles.HowItWorksModal_buttons)}>
              <Button.Ring
                className={clsx(styles.HowItWorksModal_button, styles.prev)}
                styles="green"
                disabled={activeSlide === 0}
                onClick={() => {
                  refSwiper.current?.slidePrev()
                  scrollTo("#modal-stone-flex-container-content")
                }}
              >
                <ArrowLeftIcon />
              </Button.Ring>
              <Button
                className={clsx(styles.HowItWorksModal_button)}
                styles="orange-3d"
                onClick={closeModal}
              >
                {t("button.close")}
              </Button>
              <Button.Ring
                className={clsx(styles.HowItWorksModal_button, styles.next)}
                styles="green"
                disabled={activeSlide === SLIDES.length - 1}
                onClick={() => {
                  refSwiper.current?.slideNext()
                  scrollTo("#modal-stone-flex-container-content")
                }}
              >
                <ArrowLeftIcon />
              </Button.Ring>
            </div>
          </>
        }
      >
        <div className={clsx(styles.HowItWorksModal_slides)}>
          <Swiper
            className={clsx(styles.HowItWorksModal_swiper)}
            autoHeight={false}
            slidesPerView={1}
            spaceBetween={30}
            grabCursor={true}
            allowTouchMove={true}
            onSwiper={(swiper) => {
              refSwiper.current = swiper
            }}
            onSlideChange={(e) => {
              setActiveSlide(e.activeIndex)
            }}
          >
            {SLIDES.map(({ key, Component }) => (
              <SwiperSlide key={key}>
                <Component language={language} />
              </SwiperSlide>
            ))}
          </Swiper>
          <div className={clsx(styles.HowItWorksModal_indicator)}>
            {SLIDES.map(({ key }, index) => (
              <div
                className={clsx(styles.HowItWorksModal_indicator_dot, {
                  [styles.active]: index === activeSlide
                })}
                key={key}
              />
            ))}
          </div>
        </div>
      </Modal.StoneFlexContainer>
    </Modal.Center>
  )
}
